import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { C, S } from './cedsStyles';

const BENCHMARK_TEMPLATES = [
  {
    key: 'housing_units',
    title: 'Annual Housing Units Target',
    goal: 'Goal 1, Objective 1.1',
    placeholder: '[X units — to be set after baseline data is established]',
    question: "What's the maximum our contractors and lenders could actually process per year?",
  },
  {
    key: 'childcare_slots',
    title: 'Licensed Childcare Slots / Zero-Childcare Communities',
    goal: 'Goal 1, Objective 1.2',
    placeholder: '[current number — to be established in Year 1]',
    question: 'How many new licensed operations are realistic in 5 years, given startup barriers you have seen?',
  },
  {
    key: 'business_succession',
    title: 'Business Succession Transitions',
    goal: 'Goal 2, Objective 2.1',
    placeholder: '15 transitions by 2030',
    question: 'How many owners do you personally know who are within 10 years of retirement with no succession plan?',
  },
  {
    key: 'workforce_apprentice',
    title: 'Active Apprenticeships / Work-Based Learning',
    goal: 'Goal 2, Objective 2.2',
    placeholder: '[X — to be set after baseline is established in Year 1]',
    question: 'What is employer appetite for structured apprenticeships in your sector right now?',
  },
  {
    key: 'infrastructure_pipe',
    title: 'Infrastructure Projects Advancing from Planning to Funded',
    goal: 'Goal 3',
    placeholder: '(no explicit target set)',
    question: 'How many communities in your area have projects ready to move if grant support were available?',
  },
  {
    key: 'frontier_equity',
    title: 'Frontier County / Geographic Equity Target',
    goal: 'Goal 1 + Goal 4',
    placeholder: '(no explicit target set)',
    question: 'What % of PADD housing and workforce activity should reach counties outside Scotts Bluff County?',
  },
];

const SECTOR_LABELS = {
  public_sector:    'Public Sector',
  private_business: 'Private Business',
  education:        'Education',
  nonprofit:        'Nonprofit',
  agriculture:      'Agriculture',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ─── Tab: Council Members ─────────────────────────────────────────────────────

function MembersTab() {
  const [members, setMembers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [iEmail, setIEmail]       = useState('');
  const [iName, setIName]         = useState('');
  const [iOrg, setIOrg]           = useState('');
  const [iCounty, setICounty]     = useState('');
  const [iSector, setISector]     = useState('public_sector');
  const [iErr, setIErr]           = useState('');
  const [inviting, setInviting]   = useState(false);
  const [iSuccess, setISuccess]   = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [logins, setLogins]       = useState({});

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('ceds_council_members')
      .select('*')
      .order('full_name');
    setMembers(data ?? []);
    setLoading(false);

    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/list-member-logins', {
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    });
    if (res.ok) {
      const body = await res.json();
      setLogins(body.logins ?? {});
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleActive(m) {
    await supabase.from('ceds_council_members')
      .update({ is_active: !m.is_active })
      .eq('id', m.id);
    load();
  }

  async function handleDelete(m) {
    if (!window.confirm(`Permanently delete ${m.full_name}?\n\nThis removes their council record AND their login account. They will need to be re-invited to regain access.`)) return;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/delete-council-member', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ memberId: m.id }),
    });
    const body = await res.json();
    if (!res.ok) { alert(`Delete failed: ${body.error}`); return; }
    load();
  }

  async function handleResend(m) {
    if (!window.confirm(`Resend invite to ${m.full_name}?\n\nThis generates a brand-new login for them with a fresh invite link. Use this if their original invite link expired before they could log in.`)) return;
    setResendingId(m.id);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/resend-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ memberId: m.id }),
    });
    const body = await res.json();
    setResendingId(null);
    if (!res.ok) { alert(`Resend failed: ${body.error}`); return; }
    setISuccess(`Invite resent to ${m.full_name}.`);
    load();
  }

  async function handleInvite() {
    setIErr(''); setISuccess('');
    if (!iEmail.trim())  { setIErr('Email is required.'); return; }
    if (!iName.trim())   { setIErr('Full name is required.'); return; }
    if (!iOrg.trim())    { setIErr('Organization is required.'); return; }
    if (!iCounty.trim()) { setIErr('County is required.'); return; }

    setInviting(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/invite-council-member', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({
        email:        iEmail.trim(),
        full_name:    iName.trim(),
        organization: iOrg.trim(),
        county:       iCounty.trim(),
        sector:       iSector,
      }),
    });
    const body = await res.json();
    setInviting(false);

    if (!res.ok) { setIErr(body.error ?? 'Invite failed.'); return; }

    const invitedEmail = iEmail.trim();
    setIEmail(''); setIName(''); setIOrg(''); setICounty(''); setISector('public_sector');
    setShowInvite(false);
    setISuccess(`Invite sent to ${invitedEmail}. They'll receive an email with a link to set their password.`);
    await load();
  }

  if (loading) return <div style={{ color: C.muted }}>Loading members…</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => { setShowInvite(v => !v); setIErr(''); setISuccess(''); }} style={S.btn.gold}>
          {showInvite ? 'Cancel' : '+ Invite New Member'}
        </button>
      </div>

      {iSuccess && (
        <div style={{ color: '#155724', background: '#d4edda', border: '1px solid #c3e6cb',
          borderRadius: 4, padding: '9px 14px', fontSize: 13, marginBottom: 16 }}>
          {iSuccess}
        </div>
      )}

      {showInvite && (
        <div style={{ ...S.card, borderLeft: `4px solid ${C.gold}`, background: C.cream, marginBottom: 24 }}>
          <h3 style={{ color: C.navy, margin: '0 0 6px', fontSize: '1rem' }}>Invite New Council Member</h3>
          <p style={{ color: C.muted, fontSize: 13, margin: '0 0 16px' }}>
            Fills in their details, sends the invite email, and registers their account — all in one step.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={S.label}>Email Address *</label>
              <input value={iEmail} onChange={e => setIEmail(e.target.value)} style={S.input}
                placeholder="jane@example.com" type="email" />
            </div>
            <div>
              <label style={S.label}>Full Name *</label>
              <input value={iName} onChange={e => setIName(e.target.value)} style={S.input} placeholder="Jane Smith" />
            </div>
            <div>
              <label style={S.label}>Organization *</label>
              <input value={iOrg} onChange={e => setIOrg(e.target.value)} style={S.input} placeholder="City of Scottsbluff" />
            </div>
            <div>
              <label style={S.label}>County *</label>
              <input value={iCounty} onChange={e => setICounty(e.target.value)} style={S.input} placeholder="Scotts Bluff" />
            </div>
            <div>
              <label style={S.label}>Sector *</label>
              <select value={iSector} onChange={e => setISector(e.target.value)} style={S.input}>
                {Object.entries(SECTOR_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {iErr && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{iErr}</div>}

          <button onClick={handleInvite} disabled={inviting}
            style={{ ...S.btn.primary, opacity: inviting ? 0.65 : 1 }}>
            {inviting ? 'Sending invite…' : 'Send Invite & Register'}
          </button>
        </div>
      )}

      {members.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', color: C.muted, padding: '40px 24px' }}>
          No council members yet. Use the button above to invite the first one.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.rule}` }}>
                {['Full Name', 'County', 'Sector', 'Role', 'Active', 'Last Login', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.navy, fontWeight: 700, fontSize: 12 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id} style={{ borderBottom: `1px solid ${C.rule}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: C.navy }}>{m.full_name}</td>
                  <td style={{ padding: '10px 12px', color: C.muted }}>{m.county}</td>
                  <td style={{ padding: '10px 12px', color: C.muted }}>{SECTOR_LABELS[m.sector] ?? m.sector}</td>
                  <td style={{ padding: '10px 12px', color: C.muted }}>
                    {m.role === 'padd_admin' ? 'PADD Admin' : 'Council Member'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                      fontSize: 12, fontWeight: 600,
                      color:      m.is_active ? '#155724' : C.muted,
                      background: m.is_active ? '#d4edda' : '#eeeeee',
                    }}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: C.muted, whiteSpace: 'nowrap' }}>
                    {logins[m.id] ? fmtDateTime(logins[m.id]) : 'Never'}
                  </td>
                  <td style={{ padding: '10px 12px', display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleResend(m)}
                      disabled={resendingId === m.id}
                      style={{
                        ...S.btn.secondary, fontSize: 12, padding: '4px 10px',
                        color: C.accent, borderColor: C.accent,
                        opacity: resendingId === m.id ? 0.6 : 1,
                      }}
                    >
                      {resendingId === m.id ? 'Resending…' : 'Resend Invite'}
                    </button>
                    <button
                      onClick={() => toggleActive(m)}
                      style={{
                        ...S.btn.secondary, fontSize: 12, padding: '4px 10px',
                        color:       m.is_active ? C.red : C.accent,
                        borderColor: m.is_active ? C.red : C.accent,
                      }}
                    >
                      {m.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(m)}
                      style={{ ...S.btn.secondary, fontSize: 12, padding: '4px 10px', color: C.red, borderColor: C.red }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Benchmark Assignments ───────────────────────────────────────────────

function AssignmentsTab() {
  const [assignments, setAssignments]   = useState([]);
  const [members, setMembers]           = useState([]);
  const [submittedIds, setSubmittedIds] = useState(new Set());
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [aMemberId, setAMemberId]       = useState('');
  const [aBenchKey, setABenchKey]       = useState(BENCHMARK_TEMPLATES[0].key);
  const [aDueDate, setADueDate]         = useState('');
  const [aPaddData, setAPaddData]       = useState('');
  const [aErr, setAErr]                 = useState('');
  const [adding, setAdding]             = useState(false);

  const load = useCallback(async () => {
    const [{ data: asgns }, { data: mems }, { data: subs }] = await Promise.all([
      supabase
        .from('ceds_benchmark_assignments')
        .select('*, ceds_council_members(full_name, county)')
        .order('created_at', { ascending: false }),
      supabase
        .from('ceds_council_members')
        .select('id, full_name, county')
        .eq('is_active', true)
        .order('full_name'),
      supabase
        .from('ceds_worksheet_submissions')
        .select('assignment_id')
        .neq('status', 'superseded'),
    ]);
    setAssignments(asgns ?? []);
    setMembers(mems ?? []);
    setSubmittedIds(new Set((subs ?? []).map(s => s.assignment_id)));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(a) {
    const label = `${a.ceds_council_members?.full_name} — ${a.benchmark_title}`;
    if (!window.confirm(`Delete this assignment?\n\n${label}\n\nThis will also delete any submitted worksheets for it.`)) return;
    await supabase.from('ceds_worksheet_submissions').delete().eq('assignment_id', a.id);
    const { error } = await supabase.from('ceds_benchmark_assignments').delete().eq('id', a.id);
    if (error) { alert(`Error: ${error.message}`); return; }
    load();
  }

  async function handleAdd() {
    setAErr('');
    if (!aMemberId) { setAErr('Please select a member.'); return; }

    const tmpl = BENCHMARK_TEMPLATES.find(t => t.key === aBenchKey);
    setAdding(true);
    const { error } = await supabase.from('ceds_benchmark_assignments').insert({
      member_id:        aMemberId,
      benchmark_key:    tmpl.key,
      benchmark_title:  tmpl.title,
      ceds_goal:        tmpl.goal,
      placeholder_text: tmpl.placeholder,
      question_prompt:  tmpl.question,
      padd_data:        aPaddData.trim() || null,
      due_date:         aDueDate || null,
    });
    setAdding(false);

    if (error) {
      setAErr(error.code === '23505'
        ? 'This member already has that benchmark assigned.'
        : `Error: ${error.message}`);
      return;
    }
    setAMemberId(''); setABenchKey(BENCHMARK_TEMPLATES[0].key);
    setADueDate(''); setAPaddData('');
    setShowAdd(false);
    load();
  }

  const selectedTmpl = BENCHMARK_TEMPLATES.find(t => t.key === aBenchKey);

  if (loading) return <div style={{ color: C.muted }}>Loading assignments…</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowAdd(v => !v)} style={S.btn.gold}>
          {showAdd ? 'Cancel' : '+ Add Assignment'}
        </button>
      </div>

      {showAdd && (
        <div style={{ ...S.card, borderLeft: `4px solid ${C.gold}`, background: C.cream, marginBottom: 24 }}>
          <h3 style={{ color: C.navy, margin: '0 0 16px', fontSize: '1rem' }}>Add Benchmark Assignment</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={S.label}>Council Member *</label>
              <select value={aMemberId} onChange={e => setAMemberId(e.target.value)} style={S.input}>
                <option value="">— select member —</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.full_name} ({m.county})</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Benchmark *</label>
              <select value={aBenchKey} onChange={e => setABenchKey(e.target.value)} style={S.input}>
                {BENCHMARK_TEMPLATES.map(t => (
                  <option key={t.key} value={t.key}>{t.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Due Date</label>
              <input type="date" value={aDueDate} onChange={e => setADueDate(e.target.value)} style={S.input} />
            </div>
            <div>
              <label style={S.label}>PADD Data (optional)</label>
              <input value={aPaddData} onChange={e => setAPaddData(e.target.value)} style={S.input}
                placeholder="Baseline data to share with this member" />
            </div>
          </div>

          {selectedTmpl && (
            <div style={{
              background: '#f0f0ef', borderRadius: 4,
              padding: '12px 16px', marginBottom: 16, fontSize: 13, color: C.muted,
            }}>
              <div style={{ marginBottom: 4 }}><strong style={{ color: C.navy }}>CEDS Goal:</strong> {selectedTmpl.goal}</div>
              <div style={{ marginBottom: 4 }}><strong style={{ color: C.navy }}>Question:</strong> {selectedTmpl.question}</div>
              <div><strong style={{ color: C.navy }}>Draft language:</strong> {selectedTmpl.placeholder}</div>
            </div>
          )}

          {aErr && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{aErr}</div>}

          <button onClick={handleAdd} disabled={adding}
            style={{ ...S.btn.primary, opacity: adding ? 0.65 : 1 }}>
            {adding ? 'Saving…' : 'Add Assignment'}
          </button>
        </div>
      )}

      {assignments.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', color: C.muted, padding: '40px 24px' }}>
          No assignments yet. Use the button above to assign benchmarks to council members.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.rule}` }}>
                {['Member', 'County', 'Benchmark', 'CEDS Goal', 'Due Date', 'Submitted?', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.navy, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assignments.map(a => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${C.rule}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: C.navy }}>
                    {a.ceds_council_members?.full_name}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.muted }}>
                    {a.ceds_council_members?.county}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.navy }}>
                    {a.benchmark_title}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.muted, fontSize: 12 }}>
                    {a.ceds_goal}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.muted, whiteSpace: 'nowrap' }}>
                    {fmtDate(a.due_date)}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {submittedIds.has(a.id)
                      ? <span style={{ color: '#155724', fontWeight: 600, fontSize: 13 }}>✓ Yes</span>
                      : <span style={{ color: C.muted, fontSize: 13 }}>No</span>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      onClick={() => handleDelete(a)}
                      style={{ ...S.btn.secondary, fontSize: 12, padding: '4px 10px', color: C.red, borderColor: C.red }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Access Log ──────────────────────────────────────────────────────────

function AccessLogTab() {
  const [log, setLog]         = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('ceds_document_access_log')
        .select('*, ceds_documents(title), ceds_council_members(full_name)')
        .order('accessed_at', { ascending: false })
        .limit(100);
      setLog(data ?? []);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ color: C.muted }}>Loading access log…</div>;

  if (!log.length) {
    return (
      <div style={{ ...S.card, textAlign: 'center', color: C.muted, padding: '40px 24px' }}>
        No document access recorded yet.
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${C.rule}` }}>
            {['Member', 'Document', 'Action', 'Timestamp'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.navy, fontWeight: 700, fontSize: 12 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {log.map(entry => (
            <tr key={entry.id} style={{ borderBottom: `1px solid ${C.rule}` }}>
              <td style={{ padding: '10px 12px', fontWeight: 600, color: C.navy }}>
                {entry.ceds_council_members?.full_name ?? '—'}
              </td>
              <td style={{ padding: '10px 12px', color: C.navy }}>
                {entry.ceds_documents?.title ?? '—'}
              </td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                  fontSize: 12, fontWeight: 600,
                  color:      entry.action === 'download' ? '#004085' : C.muted,
                  background: entry.action === 'download' ? '#cce5ff' : '#eeeeee',
                }}>
                  {entry.action}
                </span>
              </td>
              <td style={{ padding: '10px 12px', color: C.muted, fontSize: 13, whiteSpace: 'nowrap' }}>
                {fmtDateTime(entry.accessed_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab: Module Videos ───────────────────────────────────────────────────────

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

function VideosTab() {
  const [moduleNums, setModuleNums] = useState([]);
  const [videoMap, setVideoMap]     = useState({});
  const [inputs, setInputs]         = useState({});   // moduleNumber -> { url, title }
  const [saving, setSaving]         = useState(null); // module number being saved
  const [err, setErr]               = useState({});
  const [ok, setOk]                 = useState({});

  const load = useCallback(async () => {
    const [{ data: threads }, { data: vids }] = await Promise.all([
      supabase.from('ceds_discussion_threads').select('module_number').eq('is_active', true),
      supabase.from('ceds_module_config').select('module_number, video_url, video_title'),
    ]);
    const nums = [...new Set((threads ?? []).map(t => t.module_number ?? 1))].sort((a, b) => a - b);
    const maxMod = nums.length > 0 ? Math.max(...nums) : 1;
    // Show all existing modules plus the next one (for future use)
    const allNums = [...new Set([...nums, maxMod + 1])];
    setModuleNums(allNums);

    const vm = {};
    (vids ?? []).forEach(v => { vm[v.module_number] = v; });
    setVideoMap(vm);

    const init = {};
    allNums.forEach(n => {
      init[n] = { url: vm[n]?.video_url ?? '', title: vm[n]?.video_title ?? '' };
    });
    setInputs(init);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(modNum) {
    setErr(e => ({ ...e, [modNum]: '' }));
    setOk(o => ({ ...o, [modNum]: '' }));
    const { url, title } = inputs[modNum] ?? {};

    if (url && !getYouTubeId(url)) {
      setErr(e => ({ ...e, [modNum]: 'Not a recognised YouTube URL. Paste the full youtube.com/watch or youtu.be link.' }));
      return;
    }

    setSaving(modNum);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/set-module-video', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ moduleNumber: modNum, videoUrl: url || null, videoTitle: title || '' }),
    });
    const body = await res.json();
    setSaving(null);

    if (!res.ok) {
      setErr(e => ({ ...e, [modNum]: body.error ?? 'Save failed.' }));
      return;
    }

    setOk(o => ({ ...o, [modNum]: url ? 'Video saved!' : 'Video cleared.' }));
    await load();
  }

  return (
    <div>
      <p style={{ color: C.muted, fontSize: 14, margin: '0 0 24px' }}>
        Attach a YouTube video to any module — it appears above that module's discussion prompts for all members.
        Paste the full YouTube URL (youtube.com/watch or youtu.be link).
      </p>

      {moduleNums.map(modNum => {
        const current = videoMap[modNum];
        const inp = inputs[modNum] ?? { url: '', title: '' };
        const isSaving = saving === modNum;

        return (
          <div key={modNum} style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: C.navy, fontSize: 15, marginBottom: 12 }}>
              Module {modNum}
              {current && (
                <span style={{
                  marginLeft: 10, fontSize: 11, fontWeight: 600,
                  color: '#155724', background: '#d4edda',
                  padding: '2px 8px', borderRadius: 10,
                }}>
                  Video set
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={S.label}>YouTube URL</label>
                <input
                  value={inp.url}
                  onChange={e => setInputs(i => ({ ...i, [modNum]: { ...i[modNum], url: e.target.value } }))}
                  style={{ ...S.input, marginTop: 4 }}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={S.label}>Title (optional — shown above the video)</label>
                <input
                  value={inp.title}
                  onChange={e => setInputs(i => ({ ...i, [modNum]: { ...i[modNum], title: e.target.value } }))}
                  style={{ ...S.input, marginTop: 4 }}
                  placeholder="e.g. Module 2 Introduction — Kyle Keller"
                />
              </div>
            </div>

            {err[modNum] && (
              <div style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>{err[modNum]}</div>
            )}
            {ok[modNum] && (
              <div style={{ color: '#155724', fontSize: 13, marginBottom: 10 }}>{ok[modNum]}</div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => handleSave(modNum)}
                disabled={isSaving}
                style={{ ...S.btn.primary, opacity: isSaving ? 0.65 : 1 }}
              >
                {isSaving ? 'Saving…' : inp.url ? 'Save Video' : 'Clear Video'}
              </button>
              {current && (
                <button
                  onClick={() => {
                    setInputs(i => ({ ...i, [modNum]: { url: '', title: '' } }));
                    handleSave(modNum);
                  }}
                  style={{ ...S.btn.secondary, color: C.red, borderColor: C.red }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'members',     label: 'Council Members' },
  { key: 'assignments', label: 'Benchmark Assignments' },
  { key: 'log',         label: 'Access Log' },
  { key: 'videos',      label: 'Module Videos' },
];

export default function AdminPage() {
  const navigate          = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tab, setTab]     = useState('members');

  useEffect(() => {
    async function init() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { navigate('/ceds/login'); return; }
      const { data: m } = await supabase
        .from('ceds_council_members')
        .select('role')
        .eq('id', session.user.id)
        .maybeSingle();
      if (m?.role !== 'padd_admin') { navigate('/ceds/portal/discussions'); return; }
      setChecking(false);
    }
    init();
  }, [navigate]);

  if (checking) return <div style={{ color: C.muted, paddingTop: 60, textAlign: 'center' }}>Loading…</div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.navy, margin: '0 0 4px', fontSize: '1.5rem' }}>Admin</h2>
        <p style={{ color: C.muted, margin: 0, fontSize: 14 }}>
          Manage council members, benchmark assignments, and document access.
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `2px solid ${C.rule}`, marginBottom: 28 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 14,
            fontWeight:   tab === t.key ? 700 : 400,
            color:        tab === t.key ? C.navy : C.muted,
            borderBottom: tab === t.key ? `3px solid ${C.gold}` : '3px solid transparent',
            marginBottom: -2,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'members'     && <MembersTab />}
      {tab === 'assignments' && <AssignmentsTab />}
      {tab === 'log'         && <AccessLogTab />}
      {tab === 'videos'      && <VideosTab />}
    </div>
  );
}
